/**
 * AffLayerProvider
 * 
 * Implementation of the LayerProvider interface for Aircraft Flight Following (AFF) data.
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayerProvider } from '../BaseLayerProvider';
import { applyOpacityToMarkers } from '../markerOpacity';
import {
  AFF_SHOW_TRACK_EVENT,
  AFF_CLEAR_TRACKS_EVENT,
  AFF_LOCATE_POINT_EVENT,
  type AffShowTrackDetail,
} from '../../../components/explorers/affExplorer/affTrackEvent';
import { rememberTrack, forgetAllTracks, drawnTracks } from './affTrackRegistry';
import {
  buildTailCollection,
  tailLayerSpecs,
  AFF_TAIL_SOURCE_ID,
  AFF_TAIL_LAYER_ID,
  AFF_TAIL_LIFTED_LAYER_ID,
} from './affAircraftTail';
import { dispatchAffTrackDrawn, dispatchAffTrackRemoved, AFF_TRACK_RETIME_EVENT, AffTrackRetimeDetail, AFF_LIVE_WINDOW_EVENT, AffLiveWindowDetail } from '../../../components/explorers/affExplorer/affTrackEvent';
import { aircraftStatus } from './affAircraftStatus';
import { buildTrackUrl, type TrackSpan } from './affTrackUrl';
import { dispatchAffShowAircraft } from '../../../components/explorers/affExplorer/affTrackEvent';
import {
  nextTrackColor,
  haloColorFor,
  TRACK_LINE_WIDTH,
  TRACK_HALO_WIDTH,
  TRACK_HALO_BLUR,
  TRACK_HALO_OPACITY,
} from './affTrackColors';
import {
  LayerDataConfig,
  LayerProcessingConfig,
  LayerRenderingConfig,
  LayerOptions,
  LayerHelpers,
  CleanupReason
} from '../interfaces';
import { logger } from '../../../utils/logger';
import { affTrackLayerIds } from '../../layers2/AffTrackLayer';
import { getBackendUrl } from '../../../services/configService';
import { affIconUrl } from './affSymbology';
import {
  registrationLabel,
  createLabelElement,
  resolveLabelCollisions,
  labelsVisibleAtZoom,
} from './affMarkerLabel';
import { markerRowHoverText, clickActionFor } from './affAircraftInteractions';
import { drawAircraftModels, removeAircraftModels, rescaleAircraftModels, MODEL_LAYER_ID, type AircraftForModel } from './affAircraftModels';
import { isHeartbeatRow } from './affHeartbeatPopup';
import { MODE_3D_EVENT } from '../../mapcontrols/Map3DToggle/Map3DToggle';
import { isMap3D } from '../../mapcontrols/Map3DToggle/map3DMode';

const log = logger('AffLayerProvider');

// Add type definition for window to include AFF markers and tracks
declare global {
  interface Window {
    affMarkers?: mapboxgl.Marker[];
    affTracks?: string[];
    clearAircraftTracks?: (map: mapboxgl.Map) => void;
  }
}

// Function to clear aircraft tracks
/**
 * Registration labels currently on the map (#781), with what they need to
 * arbitrate between themselves when they collide.
 */
interface AffLabelEntry {
  esn: string;
  status: 'flying' | 'landed';
  el: HTMLElement;
}

let affLabelEntries: AffLabelEntry[] = [];
let affLabelViewHandler: (() => void) | null = null;

/**
 * Hide the registration labels that cannot be shown (#781).
 *
 * DOM markers get no collision handling from mapbox, so this runs on every
 * view change: below the label zoom nothing is drawn at all, and above it the
 * labels are measured where they actually landed and the losers hidden.
 *
 * Measurement has to happen with the labels shown — a hidden element has no
 * box — so everything is revealed first and then culled.
 */
function applyAffLabelVisibility(map: mapboxgl.Map): void {
  if (!affLabelEntries.length) return;

  if (!labelsVisibleAtZoom(map.getZoom())) {
    affLabelEntries.forEach((entry) => { entry.el.hidden = true; });
    return;
  }

  affLabelEntries.forEach((entry) => { entry.el.hidden = false; });

  const kept = new Set(
    resolveLabelCollisions(
      affLabelEntries.map((entry) => ({
        esn: entry.esn,
        status: entry.status,
        rect: entry.el.getBoundingClientRect(),
      })),
    ),
  );

  affLabelEntries.forEach((entry) => { entry.el.hidden = !kept.has(entry.esn); });
}

/** Follow the view so labels re-arbitrate as aircraft converge and separate. */
function bindAffLabelViewHandler(map: mapboxgl.Map): void {
  if (affLabelViewHandler) map.off('move', affLabelViewHandler);

  affLabelViewHandler = () => applyAffLabelVisibility(map);
  map.on('move', affLabelViewHandler);
}

function unbindAffLabelViewHandler(map: mapboxgl.Map): void {
  if (affLabelViewHandler) {
    map.off('move', affLabelViewHandler);
    affLabelViewHandler = null;
  }
  affLabelEntries = [];
}

const clearAircraftTracks = (_map: mapboxgl.Map): void => {
  // Each drawn track is a registry layer (#802), so removal goes through the
  // drawer: it destroys the layer, which takes the mapbox layers and the source
  // off the map and drops the drawer entry in the same step.
  for (const track of drawnTracks()) {
    dispatchAffTrackRemoved({ trackId: track.trackId });
  }
  // Otherwise a later style change would resurrect tracks the user cleared.
  forgetAllTracks();
};

/**
 * Map-action state lives at module level, not on the layer provider.
 *
 * These listeners were previously attached inside the provider's marker
 * rendering, so they existed only while the AFF live layer was switched on —
 * with the layer off (its default) Locate, Show Track and Clear Tracks
 * silently did nothing. They belong to the MAP, and are attached from
 * mapUtils when the map is created.
 */
/**
 * The map the AFF actions currently act on (#815).
 *
 * The handlers below are attached to `window` once and then live for the life
 * of the module. Capturing `map` in their closures meant that when the map was
 * re-created they carried on drawing onto the original object: sources and
 * layers were added successfully, the fit ran with real coordinates, and none
 * of it was on the map the user could see. Reading the current map at call time
 * instead keeps one set of listeners while always acting on what is rendered.
 */
let currentMap: mapboxgl.Map | null = null;

let showTrackHandler: ((event: Event) => void) | null = null;
let retimeHandler: ((event: Event) => void) | null = null;
let styleLoadHandler: (() => void) | null = null;
let clearTracksHandler: (() => void) | null = null;
let locatePointHandler: ((event: Event) => void) | null = null;
let locateMarker: mapboxgl.Marker | null = null;
let mode3dHandler: ((event?: { is3D: boolean }) => void) | null = null;
let zoomHandler: (() => void) | null = null;
let idleHandler: (() => void) | null = null;
let modelClickHandler: ((event: mapboxgl.MapLayerMouseEvent) => void) | null = null;
let modelEnterHandler: ((event: mapboxgl.MapLayerMouseEvent) => void) | null = null;
let modelLeaveHandler: (() => void) | null = null;
/** The hover popup open over a 3D model, if any. */
let modelHoverPopup: mapboxgl.Popup | null = null;
/** The aircraft from the last refresh, kept so a switch to 3D can draw them as models (#820). */
let lastModelAircraft: AircraftForModel[] = [];
/**
 * Aircraft the last model draw could not place for want of terrain under them
 * (#820 defect a) — off screen, where mapbox has loaded none. Kept so they can
 * be drawn once the view reaches them, rather than at the next refresh.
 */
let skippedModelAircraft: AircraftForModel[] = [];
/** Whether the map is in 3D, so a refresh knows to redraw models rather than show markers. */
let is3DMode = false;

/** Draw the models, and remember which could not yet be placed. */
function drawModels(map: mapboxgl.Map): void {
  // No aircraft (the layer is off, or none are reporting): no model layer, rather
  // than an empty one a basemap change keeps adding back.
  if (lastModelAircraft.length === 0) {
    removeAircraftModels(map);
    skippedModelAircraft = [];
    return;
  }
  skippedModelAircraft = drawAircraftModels(map, lastModelAircraft);
}

/**
 * Every AFF hover popup open right now (#827), closed by whatever takes its
 * marker or model away: a refresh, the switch to 3D or the layer going off
 * fire no mouseleave.
 */
const hoverPopups = new Set<mapboxgl.Popup>();

/**
 * The hover text is plain text built from Selkirk-sourced fields (name, label,
 * callsign), so it is set as text, never parsed as markup (#866).
 */
function openHoverPopup(map: mapboxgl.Map, coords: [number, number], text: string): mapboxgl.Popup {
  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
    className: 'hover-popup aff-hover-popup',
    offset: 15,
  })
    .setLngLat(coords)
    .setText(text)
    .addTo(map);
  hoverPopups.add(popup);
  return popup;
}

function closeHoverPopup(popup: mapboxgl.Popup | null | undefined): void {
  if (!popup) return;
  popup.remove();
  hoverPopups.delete(popup);
}

function closeHoverPopups(): void {
  hoverPopups.forEach((popup) => popup.remove());
  hoverPopups.clear();
  modelHoverPopup = null;
}

/** 2D markers and 3D models never show together (#820). */
function setAircraftMarkersShown(shown: boolean): void {
  // A hidden marker never gets its mouseleave, so its popup would outlive it.
  if (!shown) closeHoverPopups();
  (window.affMarkers ?? []).forEach((marker: mapboxgl.Marker) => {
    const el = marker.getElement();
    // The heartbeat has no model; its marker shows in both modes.
    if (el.dataset.affHeartbeat === 'true') return;
    el.style.display = shown ? '' : 'none';
  });
}

// Add the clearAircraftTracks function to window for global access
if (typeof window !== 'undefined') {
  window.clearAircraftTracks = clearAircraftTracks;
}

export class AffLayerProvider extends BaseLayerProvider {

  /**
   * Create a new AffLayerProvider
   */
  constructor() {
    const BACKEND_URL = getBackendUrl();

    // Define the data configuration
    const dataConfig: LayerDataConfig = {
      name: "Live AFF",
      shortName: "LiveAFF",
      mainIcon: "fa-solid fa-plane-up",
      sources: {
        "aff": {
          url: `${BACKEND_URL}/api/aff/ac`,
          type: 'geojson'
        }
      }
    };

    // Define the processing configuration
    const processingConfig: LayerProcessingConfig = {
      mainSource: {
        sourceName: "aff",
        idKey: "id"
      }
    };

    // Determine aircraft type based on the operational function
    const getAircraftType = (feature: GeoJSON.Feature): string => {
      if (!feature.properties) return 'CL';
      
      const operationalFunction = feature.properties.operationalFunction;
      const model = feature.properties.model;

      const RW = ['Light', 'Intermediate', 'Medium', 'Heavy'];
      const BD = ['Birddog', 'Detection', 'Aircraft'];
      const TK = ['Tanker', 'Airtanker'];

      if (RW.includes(operationalFunction)) return 'RW';
      if (BD.includes(operationalFunction)) return 'BD';
      if (TK.includes(operationalFunction)) {
        if (model && typeof model === 'string' && model.startsWith('CL-')) return 'CL';
        if (model && typeof model === 'string' && model.startsWith('AT-802A')) return 'FB';
        return 'TK';
      }

      return 'CL'; // Default
    };

    // Determine aircraft status — see affAircraftStatus (#807).
    const getAircraftStatus = (feature: GeoJSON.Feature): 'flying' | 'landed' =>
      aircraftStatus(feature.properties);

    // Icon paths come from the shared affSymbology module (single source of
    // truth shared with the legend) — see affIconUrl below.

    // Define icon sizes for different aircraft types
    const iconSizes: Record<string, [number, number]> = {
      flyingRW: [30, 30],
      landedRW: [30, 30],
      flyingBD: [30, 30],
      landedBD: [30, 30],
      flyingTK: [50, 50],
      landedTK: [50, 50],
      flyingCL: [40, 40],
      landedCL: [40, 40],
      flyingFB: [40, 40],
      landedFB: [40, 40],
    };

    // Define the rendering configuration with custom functions
    const renderingConfig: LayerRenderingConfig = {
      // Return empty style since we're using markers
      styleFunction: (data: GeoJSON.FeatureCollection) => ({
        type: 'circle',
        paint: {
          'circle-radius': 0,
          'circle-opacity': 0
        }
      } as mapboxgl.CircleLayerSpecification),
      
      // Create custom HTML markers for aircraft
      markerFunction: (map: mapboxgl.Map, data: GeoJSON.FeatureCollection) => {
        // First point in the lifecycle where the map instance is in hand.
        // Idempotent — attachShowTrackListener returns early if already bound.
        attachAffMapActions(map);

        log.debug('Creating AFF markers');

        // Remove any existing markers
        if (window.affMarkers && window.affMarkers.length) {
          log.debug('Removing existing AFF markers:', window.affMarkers.length);
          window.affMarkers.forEach((marker: mapboxgl.Marker) => marker.remove());
          window.affMarkers = [];
        } else {
          window.affMarkers = [];
        }

        // Labels belong to the markers just removed (#781). Rebuilt below with
        // this refresh's aircraft — keeping them would leave the collision
        // pass arbitrating between elements no longer on the map.
        affLabelEntries = [];

        // #820 — rebuilt below from this refresh, for drawing as 3D models.
        lastModelAircraft = [];

        // The markers just removed took their hover popups' pointer with them
        // (#827); close those, and any AFF click popup, with them.
        closeHoverPopups();
        document.querySelectorAll('.mapboxgl-popup.aff-popup').forEach(popup => popup.remove());

        // Create a marker for each feature
        if (data && data.features) {
          log.debug(`Processing ${data.features.length} AFF markers`);

          data.features.forEach((feature: GeoJSON.Feature) => {
            if (!feature.geometry || feature.geometry.type !== 'Point') return;

            const props = feature.properties;
            if (!props) return;

            const coords = feature.geometry.coordinates as [number, number];

            // Determine aircraft type and status
            const aircraftType = getAircraftType(feature);
            const status = getAircraftStatus(feature);

            // The collector heartbeat is a health signal, not an aircraft.
            if (!isHeartbeatRow(props)) {
              lastModelAircraft.push({
                label: String(props.name ?? props.esn),
                props,
                coordinates: coords,
                code: aircraftType,
                status,
                heading: props.heading,
                altitudeAslM: props.altitude ?? props.alt,
              });
            }

            const iconKey = status + aircraftType; // e.g., "flyingRW" or "landedTK"
            const iconUrl = affIconUrl(aircraftType, status); // shared with the legend
            const iconSize = iconSizes[iconKey] || [40, 40]; // Fallback to default size
            
            // Create custom HTML element
            const el = document.createElement('div');
            el.className = 'aff-marker';
            // #820 — the heartbeat has no 3D model, so its marker stays in 3D.
            if (isHeartbeatRow(props)) el.dataset.affHeartbeat = 'true';
            
            // Style the marker container
            el.style.width = `${iconSize[0]}px`;
            el.style.height = `${iconSize[1]}px`;
            el.style.cursor = 'pointer';
            
            // Special case: inline SVG heart so it renders even before
            // FontAwesome CSS is loaded (dev-mode async-CSS race).
            if (props.name && props.name.toLowerCase().includes('heart')) {
              el.innerHTML = `<div class="aff-heart-icon"><svg viewBox="0 0 512 512" width="24" height="24" aria-hidden="true" style="color:#bd0000;fill:currentColor;display:block;margin:auto"><path d="M47.6 300.4 228.3 469.1c7.5 7 17.4 10.9 27.7 10.9s20.2-3.9 27.7-10.9L464.4 300.4c30.4-28.3 47.6-68 47.6-109.5v-5.8c0-69.9-50.5-129.5-119.4-141C347 36.5 300.6 51.4 268 84L256 96 244 84c-32.6-32.6-79-47.5-124.6-39.9C50.5 55.6 0 115.2 0 185.1v5.8c0 41.5 17.2 81.2 47.6 109.5z"/></svg></div>`;
            } else {
              // Create an image element with rotation based on heading
              const heading = props.heading || 0;
              // Built as an element, not a string: heading is data, and a
              // non-numeric one must not reach the markup (#866).
              const img = document.createElement('img');
              img.src = iconUrl;
              img.alt = 'Aircraft';
              img.style.width = '100%';
              img.style.height = '100%';
              img.style.transform = `rotate(${Number(heading) || 0}deg)`;
              el.replaceChildren(img);
            }
            
            // #781 — the registration rides beside the icon. Appended after
            // the innerHTML above, which would otherwise overwrite it.
            const labelEl = createLabelElement(registrationLabel(props));
            if (labelEl) {
              el.appendChild(labelEl);
              affLabelEntries.push({
                esn: String(props.esn ?? props.name),
                status,
                el: labelEl,
              });
            }

            // Create the marker with exact same approach as weather markers
            const marker = new mapboxgl.Marker({
              element: el,
              anchor: 'center'
            })
            .setLngLat(coords)
            .addTo(map);
            
            // Add hover event listeners
            el.addEventListener('mouseenter', () => {
              // #781 — the full label as Selkirk composed it, call sign and
              // staleness phrase together. This previously split the string
              // on its separator, kept the head, and recomputed its own
              // relative time — discarding the half the request asks to see.
              closeHoverPopup(el._hoverPopup);
              el._hoverPopup = openHoverPopup(map, coords, markerRowHoverText(props));
            });

            el.addEventListener('mouseleave', () => {
              closeHoverPopup(el._hoverPopup);
              el._hoverPopup = null;
            });
            
            // Add click handler
            el.addEventListener('click', (e) => {
              e.stopPropagation();

              // Remove any existing popups
              document.querySelectorAll('.mapboxgl-popup.click-popup').forEach(popup => popup.remove());

              const action = clickActionFor(props, coords, iconSize[1]);

              // The heartbeat is a system health marker, not an aircraft, so it
              // keeps its dedicated popup rather than an aircraft detail view.
              if (action.kind === 'heartbeatPopup') {
                new mapboxgl.Popup({
                  closeButton: true,
                  closeOnClick: true,
                  className: 'click-popup aff-popup',
                  maxWidth: '300px',
                  offset: 25
                })
                .setLngLat(coords)
                .setHTML(action.html)
                .addTo(map);
                return;
              }

              // Aircraft open the same summary/detail modals the explorer uses.
              // This previously listed every raw property as "key: value".
              dispatchAffShowAircraft(action.aircraft, action.options);
            });
            
            // Store the marker for later removal
            window.affMarkers = window.affMarkers || [];
            window.affMarkers.push(marker);
          });
          
          log.debug(`Created ${window.affMarkers.length} AFF markers`);

          // Tails behind the active aircraft (#804), rebuilt from the same
          // payload as the markers so the two always agree.
          drawAircraftTails(map, data.features);

          // #820 — a refresh in 3D redraws the models with this refresh's
          // aircraft, and the markers it just rebuilt stay out of sight.
          if (is3DMode) {
            drawModels(map);
            setAircraftMarkersShown(false);
          }

          // #781 — arbitrate the registration labels now they are all placed,
          // then follow the view so they re-arbitrate as aircraft converge.
          applyAffLabelVisibility(map);
          bindAffLabelViewHandler(map);
        }
      },

      // Hover display function is not needed since we're using markers with direct event listeners
      hoverPopupFunction: () => null,

      // Click display function is not needed since we're using markers with direct event listeners
      clickPopupFunction: () => null,

      // Cleanup function
      cleanupFunction: (map: mapboxgl.Map, reason: CleanupReason = 'layer-off') => {
        // Map actions are NOT detached here: they belong to the map and must
        // outlive this layer being toggled off (#779).

        // Remove any AFF markers
        if (window.affMarkers && window.affMarkers.length) {
          log.debug('Removing existing AFF markers:', window.affMarkers.length);
          window.affMarkers.forEach((marker: mapboxgl.Marker) => marker.remove());
          window.affMarkers = [];
        }
        
        // #827 — nothing is left for the pointer to leave.
        closeHoverPopups();

        removeAircraftTails(map);

        // #863 — the 3D models go with the markers when the user switches the
        // layer off, but NOT on a refresh teardown: LiveLayer.refresh() runs this
        // same function every minute, and removing them there would make
        // the aircraft blink out and back in on their own. Before this, the models
        // outlived the layer entirely — aircraft stayed drawn on a map whose
        // control read "Show Live Aircraft".
        //
        // The aircraft they were drawn from go too: a basemap change (style.load)
        // or terrain loading ('idle') redraws from these lists, and would put the
        // models back on a map whose layer is off.
        if (reason !== 'refresh') {
          removeAircraftModels(map);
          lastModelAircraft = [];
          skippedModelAircraft = [];
        }

        // #781 — the labels went with their markers; stop following the view
        // for elements that no longer exist.
        unbindAffLabelViewHandler(map);

        // Drawn tracks are NOT cleared here. This runs on every autoRefresh:
        // LiveLayer.refresh() tears a markerFunction provider down and builds
        // it back up so the markers carry fresh data, which meant a track the
        // user had asked to see disappeared on its own at the next refresh.
        //
        // This function cannot tell that teardown from the user switching the
        // layer off, so it is the wrong place to dispose of tracks either way.
        // Since #802 a track is its own layer drawer entry with its own
        // visibility, opacity and removal — it is not marker state owned by
        // this layer's refresh cycle. Same reasoning as the map actions above.
        //
        // Clearing still happens where the user asks for it: the Clear Tracks
        // action, and per-entry removal from the drawer.
      }
    };

    // Define the options
    const options: LayerOptions = {
      autoRefresh: true,
      // A fallback: LiveLayer prefers the layers row's refreshInterval (migration 044).
      refreshInterval: 60_000,
      controlPosition: 'top-left'
    };

    // Call base class constructor with our configurations
    super(dataConfig, processingConfig, renderingConfig, options);

    // #822 — how long an aircraft may go without reporting before it drops off
    // the live map. The control that chooses this exists only while its layer
    // card is expanded, so it announces the choice and this provider, which
    // owns the fetch, acts on it.
    //
    // The listener is never removed: the provider is a lazy module singleton
    // (liveLayers/providers/index.ts) that lives for the life of the page, so
    // there is no teardown to hang it on.
    window.addEventListener(AFF_LIVE_WINDOW_EVENT, (event: Event) => {
      const { days } = (event as CustomEvent<AffLiveWindowDetail>).detail;
      this.applyLiveWindow(days);
    });
  }

  /**
   * Put the chosen window on the aircraft request and re-fetch.
   *
   * Built through URL rather than string concatenation so choosing a second
   * window replaces the first instead of appending to it — a request carrying
   * two maxAgeDays values is one the server is entitled to reject.
   */
  private applyLiveWindow(days: number): void {
    const url = new URL(this.dataConfig.sources.aff.url);
    url.searchParams.set('maxAgeDays', String(days));
    this.dataConfig.sources.aff.url = url.toString();

    // LiveLayer re-reads the source url on every fetch, so the next tick would
    // pick this up regardless — the refresh is what spares the user a wait of
    // up to a minute. It is a no-op until this provider is on a map.
    this.requestRefresh();
  }

  

/** #606 — dim the HTML AFF aircraft markers (paint properties don't reach them). */
  setOpacity(opacity: number): void {
    applyOpacityToMarkers(window.affMarkers, opacity);
  }

  /**
   * Kept for liveAffHelpers, which looks this method up on the provider and
   * silently does nothing if it is absent. The drawing itself is module-level
   * so the explorer's map actions do not depend on this layer being active.
   */
  async fetchAircraftTrack(esn: string, map: mapboxgl.Map, hours?: number): Promise<void> {
    return drawAircraftTrack(esn, map, { hours });
  }

}

  /**
   * Re-add every remembered track.
   *
   * mapbox `setStyle` (any basemap change) drops all custom sources and
   * layers, so a track the user asked to see silently vanishes. Redrawing on
   * style load keeps it there across basemap changes — and the halo is
   * recomputed from the stored colours, so it stays readable on the new style.
   */
function redrawRememberedTracks(_map: mapboxgl.Map): void {
    const remembered = drawnTracks();
    if (remembered.length === 0) return;

    // #802 — setStyle drops every custom layer. Redrawing used to re-add them
    // here, straight onto the map, which put the track back outside the
    // drawer's control. Announce each one instead: the drawer re-enables the
    // registry layer that already owns it, so the entry, its opacity and its
    // place in the z-order all survive a basemap change.
    for (const track of remembered) {
      dispatchAffTrackDrawn({
        trackId: track.trackId,
        esn: track.esn ?? track.trackId,
        aircraftName: track.name || track.trackId,
        data: track.data,
        color: track.color,
        halo: track.halo,
        span: track.span,
      });
    }
  }

  /**
   * Listen for the AFF Explorer's show-track request.
   *
   * Mirrors VARLayer, which listens for `intellifire:var-view-on-map` from
   * VARExplorer: the explorer stays ignorant of the map, and the layer that
   * already owns track drawing owns the listening too.
   *
   * Tracks accumulate rather than replace — comparing two aircraft over the
   * same window is a real need, and clearAircraftTracks() already exists to
   * reset them.
   */
/**
 * Draw or update the tails behind active aircraft (#804).
 *
 * One geojson source holding every tail: the set changes on each refresh, so
 * adding and removing a layer per aircraft would be slower and easier to leak.
 * Updating means setData on the existing source, not tearing it down.
 */
/**
 * The last set of tails drawn, so a basemap change can put them back.
 *
 * mapbox setStyle drops every custom source and layer. Tails are drawn from
 * one place only — markerFunction — so without this they stayed gone until the
 * next automatic refresh, up to a minute later. Tracks already survive a
 * basemap change the same way (redrawRememberedTracks).
 */
let lastTailFeatures: Array<GeoJSON.Feature | any> | null = null;

function drawAircraftTails(map: mapboxgl.Map, features: Array<GeoJSON.Feature | any>): void {
  lastTailFeatures = features;
  const collection = buildTailCollection(features);
  const existing = map.getSource(AFF_TAIL_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;

  if (existing) {
    // The layers already describe the current mode — a switch of mode re-lays
    // them (layTailLayers) — so a refresh only replaces the data. A refresh in
    // 3D must not quietly rebuild the tails flat.
    existing.setData(collection as any);
    return;
  }

  // lineMetrics makes line-progress available, which lifting point by point
  // along the line depends on (#820 step 8).
  map.addSource(AFF_TAIL_SOURCE_ID, { type: 'geojson', lineMetrics: true, data: collection as any });
  layTailLayers(map, is3DMode);
}

/**
 * Put the tail layers on the map for this mode, replacing any already there.
 *
 * Re-added rather than switched with setLayoutProperty: whether
 * line-elevation-reference can be changed on a live layer is unverified, and
 * adding a layer with the right layout is the path already seen to work.
 */
function layTailLayers(map: mapboxgl.Map, is3D: boolean): void {
  for (const id of [AFF_TAIL_LAYER_ID, AFF_TAIL_LIFTED_LAYER_ID]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  // Beneath the markers, which are HTML elements above the canvas anyway, and
  // beneath every drawn track (#866): a tail added without a beforeId landed on
  // top of the map, over a track the user asked to see.
  const beforeId = lowestDrawnTrackLayer(map);
  for (const spec of tailLayerSpecs({ is3D })) map.addLayer(spec as mapboxgl.AnyLayer, beforeId);
}

/** The bottom-most layer of any drawn track on the map, if there is one. */
function lowestDrawnTrackLayer(map: mapboxgl.Map): string | undefined {
  const trackLayers = new Set(drawnTracks().flatMap(({ trackId }) => Object.values(affTrackLayerIds(trackId))));
  if (trackLayers.size === 0) return undefined;
  return (map.getStyle?.()?.layers ?? []).find((layer) => trackLayers.has(layer.id))?.id;
}

/** Take the tails off the map when the layer is switched off. */
function removeAircraftTails(map: mapboxgl.Map): void {
  for (const id of [AFF_TAIL_LAYER_ID, AFF_TAIL_LIFTED_LAYER_ID]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(AFF_TAIL_SOURCE_ID)) map.removeSource(AFF_TAIL_SOURCE_ID);
  // Deliberately forgotten: a basemap change after the layer is switched off
  // must not bring tails back for aircraft nobody asked to see.
  lastTailFeatures = null;
}

/** Put the tails back after a basemap change has dropped them (#804). */
function redrawRememberedTails(map: mapboxgl.Map): void {
  if (!lastTailFeatures) return;
  drawAircraftTails(map, lastTailFeatures);
}

export function attachAffMapActions(map: mapboxgl.Map): void {
    // style.load is bound to the map object itself rather than to window, so it
    // has to move when the map does — left behind it would redraw remembered
    // tracks onto a map that is no longer rendered.
    if (styleLoadHandler && currentMap && currentMap !== map) {
      currentMap.off('style.load', styleLoadHandler);
      styleLoadHandler = null;
    }
    // The 3D-mode handler is bound to the map the same way, so it moves too.
    if (mode3dHandler && currentMap && currentMap !== map) {
      currentMap.off(MODE_3D_EVENT, mode3dHandler);
      mode3dHandler = null;
    }
    if (zoomHandler && currentMap && currentMap !== map) {
      currentMap.off('zoom', zoomHandler);
      zoomHandler = null;
    }
    if (idleHandler && currentMap && currentMap !== map) {
      currentMap.off('idle', idleHandler);
      idleHandler = null;
    }
    if (modelClickHandler && currentMap && currentMap !== map) {
      currentMap.off('click', MODEL_LAYER_ID, modelClickHandler);
      modelClickHandler = null;
    }
    if (modelEnterHandler && modelLeaveHandler && currentMap && currentMap !== map) {
      currentMap.off('mouseenter', MODEL_LAYER_ID, modelEnterHandler);
      currentMap.off('mouseleave', MODEL_LAYER_ID, modelLeaveHandler);
      modelEnterHandler = null;
      modelLeaveHandler = null;
    }

    // The mode belongs to the map (#866): a new map instance starts in whatever
    // mode it has recorded — 2D unless its toggle was used — not in the mode the
    // previous map was left in.
    if (currentMap !== map) is3DMode = isMap3D(map);

    currentMap = map;

    if (!modelEnterHandler || !modelLeaveHandler) {
      // #820 — hovering a model shows the same text, in the same popup, as its marker.
      modelEnterHandler = (event: mapboxgl.MapLayerMouseEvent) => {
        const feature = event.features?.[0];
        if (!currentMap || !feature || feature.geometry.type !== 'Point') return;
        const coords = feature.geometry.coordinates as [number, number];
        currentMap.getCanvas().style.cursor = 'pointer';
        closeHoverPopup(modelHoverPopup);
        modelHoverPopup = openHoverPopup(currentMap, coords, markerRowHoverText(feature.properties ?? {}));
      };
      modelLeaveHandler = () => {
        if (currentMap) currentMap.getCanvas().style.cursor = '';
        closeHoverPopup(modelHoverPopup);
        modelHoverPopup = null;
      };
      map.on('mouseenter', MODEL_LAYER_ID, modelEnterHandler);
      map.on('mouseleave', MODEL_LAYER_ID, modelLeaveHandler);
    }

    if (!modelClickHandler) {
      // #820 — a click on a model opens the same aircraft view as its marker.
      modelClickHandler = (event: mapboxgl.MapLayerMouseEvent) => {
        const feature = event.features?.[0];
        if (!feature || feature.geometry.type !== 'Point') return;
        const coords = feature.geometry.coordinates as [number, number];
        // A model has no icon; its panel anchors at the point (checked in the browser test).
        const action = clickActionFor(feature.properties ?? {}, coords, 0);
        if (action.kind === 'showAircraft') dispatchAffShowAircraft(action.aircraft, action.options);
      };
      map.on('click', MODEL_LAYER_ID, modelClickHandler);
    }

    if (!zoomHandler) {
      // #820 — the models keep their on-screen size as the map zooms.
      zoomHandler = () => {
        if (!currentMap || !is3DMode) return;
        rescaleAircraftModels(currentMap);
      };
      map.on('zoom', zoomHandler);
    }

    if (!idleHandler) {
      // #820 defect (a) — draw an aircraft skipped for want of terrain once the
      // view reaches it. 'idle' fires once the map has settled and every tile
      // has loaded, so terrain is ready; 'moveend' fires before tiles arrive.
      //
      // The guard is the point. A redraw re-renders the map, which fires 'idle'
      // again, and an aircraft permanently off screen stays permanently skipped
      // — so redrawing on every idle would loop forever. Only the skipped
      // aircraft are probed (a query, no render), and the models are redrawn
      // only when one of them has actually become placeable.
      idleHandler = () => {
        if (!currentMap || !is3DMode || skippedModelAircraft.length === 0) return;
        const map3d = currentMap;
        const nowPlaceable = skippedModelAircraft.some(
          (a) => map3d.queryTerrainElevation(a.coordinates) != null,
        );
        if (nowPlaceable) drawModels(map3d);
      };
      map.on('idle', idleHandler);
    }

    if (!mode3dHandler) {
      // #820 — in 3D the aircraft are drawn as models.
      mode3dHandler = (event?: { is3D: boolean }) => {
        if (!currentMap || !event) return;
        is3DMode = event.is3D;
        // #827 — the marker or model under the pointer is swapped for the other,
        // and neither sends a mouseleave for it.
        closeHoverPopups();
        setAircraftMarkersShown(!event.is3D);
        // #820 step 8 — the tails go up with the aircraft in 3D and come back
        // down in 2D.
        if (currentMap.getSource(AFF_TAIL_SOURCE_ID)) layTailLayers(currentMap, event.is3D);
        if (event.is3D) {
          drawModels(currentMap);
        } else {
          removeAircraftModels(currentMap);
        }
      };
      map.on(MODE_3D_EVENT, mode3dHandler);
    }

    if (!styleLoadHandler) {
      styleLoadHandler = () => {
        if (!currentMap) return;
        redrawRememberedTracks(currentMap);
        // Tails go the same way tracks do when the basemap changes.
        redrawRememberedTails(currentMap);
        // #820 — and so do the 3D models.
        if (is3DMode) drawModels(currentMap);
      };
      map.on('style.load', styleLoadHandler);
    }

    if (!locatePointHandler) {
      /**
       * Put the user on one historical position from the Tracks grid.
       *
       * Only one locate marker exists at a time: these are transient "where
       * was it at this moment" answers, and leaving a trail of them behind
       * would clutter the map with markers nobody asked to keep.
       */
      locatePointHandler = (event: Event) => {
        const d = (event as CustomEvent<{ lat?: number; lon?: number; name?: string; esn?: string; observed?: string }>).detail;
        if (d?.lat == null || d?.lon == null || Number.isNaN(d.lat) || Number.isNaN(d.lon)) return;

        // Move first. Getting the user to the position is the request; the
        // marker is a label on it, and a marker failure must not prevent the
        // map going where it was asked to go.
        const target = currentMap;
        if (!target) return;

        target.flyTo({ center: [d.lon, d.lat], zoom: Math.max(target.getZoom(), 11), duration: 800 });

        try {
          locateMarker?.remove();

          const el = document.createElement('div');
          el.className = 'aff-locate-marker';

          const label = document.createElement('div');
          label.className = 'aff-locate-popup';
          label.textContent = `${d.name ?? d.esn ?? 'Position'}${d.observed ? ` · ${d.observed}` : ''}`;

          locateMarker = new mapboxgl.Marker(el)
            .setLngLat([d.lon, d.lat])
            .setPopup(new mapboxgl.Popup({ offset: 16 }).setDOMContent(label))
            .addTo(target);
          locateMarker.togglePopup();
        } catch (error) {
          log.warn('Could not place the locate marker; the map still moved.', error);
        }
      };
      window.addEventListener(AFF_LOCATE_POINT_EVENT, locatePointHandler);
    }

    if (!clearTracksHandler) {
      clearTracksHandler = () => {
        if (!currentMap) return;
        clearAircraftTracks(currentMap);
        locateMarker?.remove();
        locateMarker = null;
      };
      window.addEventListener(AFF_CLEAR_TRACKS_EVENT, clearTracksHandler);
    }

    if (showTrackHandler) return;

    showTrackHandler = (event: Event) => {
      const detail = (event as CustomEvent<Partial<AffShowTrackDetail>>).detail;
      if (!detail?.esn) return;
      // Caught, not fire-and-forget: a failure here (backend down, config not
      // loaded, a 500) would otherwise surface as an unhandled promise
      // rejection with nothing telling anyone the track never drew.
      if (!currentMap) return;
      drawAircraftTrack(
        detail.esn,
        currentMap,
        { hours: detail.hours, start: detail.start, end: detail.end, registration: detail.registration },
        true,
        detail.name,
      ).catch((error) => {
        log.error(`Could not draw the track for ${detail.esn}`, error);
      });
    };

    window.addEventListener(AFF_SHOW_TRACK_EVENT, showTrackHandler);

    retimeHandler = (event: Event) => {
      const detail = (event as CustomEvent<AffTrackRetimeDetail>).detail;
      if (!detail?.esn || !detail?.trackId) return;
      if (!currentMap) return;

      // fitToTrack is false: the user is adjusting a track already on screen,
      // so yanking the viewport would be the opposite of what they asked for.
      drawAircraftTrack(detail.esn, currentMap, detail.span, false, detail.aircraftName, detail.trackId)
        .catch((error) => {
          log.error(`Could not redraw the track for ${detail.esn}`, error);
        });
    };

    window.addEventListener(AFF_TRACK_RETIME_EVENT, retimeHandler);
  }

export function detachAffMapActions(map?: mapboxgl.Map): void {
    currentMap = null;

    if (showTrackHandler) {
      window.removeEventListener(AFF_SHOW_TRACK_EVENT, showTrackHandler);
      showTrackHandler = null;
    }
    if (retimeHandler) {
      window.removeEventListener(AFF_TRACK_RETIME_EVENT, retimeHandler);
      retimeHandler = null;
    }
    if (styleLoadHandler) {
      map?.off('style.load', styleLoadHandler);
      styleLoadHandler = null;
    }
    if (mode3dHandler) {
      map?.off(MODE_3D_EVENT, mode3dHandler);
      mode3dHandler = null;
    }
    if (zoomHandler) {
      map?.off('zoom', zoomHandler);
      zoomHandler = null;
    }
    if (idleHandler) {
      map?.off('idle', idleHandler);
      idleHandler = null;
    }
    if (modelClickHandler) {
      map?.off('click', MODEL_LAYER_ID, modelClickHandler);
      modelClickHandler = null;
    }
    if (modelEnterHandler && modelLeaveHandler) {
      map?.off('mouseenter', MODEL_LAYER_ID, modelEnterHandler);
      map?.off('mouseleave', MODEL_LAYER_ID, modelLeaveHandler);
      modelEnterHandler = null;
      modelLeaveHandler = null;
    }
    closeHoverPopups();
    // The mode belonged to the map being detached; a new map starts in 2D.
    is3DMode = false;
    if (clearTracksHandler) {
      window.removeEventListener(AFF_CLEAR_TRACKS_EVENT, clearTracksHandler);
      clearTracksHandler = null;
    }
    if (locatePointHandler) {
      window.removeEventListener(AFF_LOCATE_POINT_EVENT, locatePointHandler);
      locatePointHandler = null;
    }
    locateMarker?.remove();
    locateMarker = null;
  }

  // Method to fetch and display aircraft track
export async function drawAircraftTrack(
  esn: string,
  map: mapboxgl.Map,
  span: TrackSpan = {},
  fitToTrack = false,
  /** Label for the drawer entry (#802). Falls back to the ESN when unknown. */
  name?: string,
  /**
   * Reuse this id instead of minting a new one (#802 AC7). A timeframe change
   * updates the track in place so its drawer entry, opacity and z-order survive.
   */
  existingTrackId?: string,
): Promise<void> {
    const BACKEND_URL = getBackendUrl();

    try {
      // Without an explicit duration this keeps calling the long-standing 48h
      // route, so the live layer's own behaviour is unchanged. The AFF Explorer
      // passes a duration and gets the parameterised sibling (#779).
      const url = buildTrackUrl(BACKEND_URL, esn, span);
      // credentials matter here: off localhost the backend is a DIFFERENT
      // ORIGIN (em3b.staging…), so without this the auth cookie is not sent
      // and the request 401s. Every other live layer already does this; this
      // fetch was the one that did not, and it only showed up on staging.
      const response = await fetch(url, { credentials: 'include' });

      if (!response.ok) {
        // Say what actually happened. The endpoints disagree on error shape —
        // the older route returns { success, msg }, the newer one
        // { error, message } — so checking data.success alone logged an empty
        // message and told nobody anything.
        log.error(
          `Track request failed for ${esn}: HTTP ${response.status} ${response.statusText} (${url})`,
        );
        return;
      }

      const data = await response.json();

      if (data.success) {
        const trackLine = JSON.parse(data.msg);

        // Generate a unique ID for this track
        const trackId = existingTrackId ?? `track-${esn}-${Date.now()}`;


        // Colour by the palette, not at random: random could hand two tracks
        // the same colour, or red and green together. See affTrackColors —
        // ordered by luminance so the distinction does not depend on hue.
        //
        // A retime keeps the colour the track already has: the drawn layer keeps
        // its colour through setData, so choosing again put the registry and
        // the map out of step (#866). A new track takes the first colour no
        // drawn track is using.
        const previous = existingTrackId
          ? drawnTracks().find((t) => t.trackId === existingTrackId)
          : undefined;
        const trackColor = previous?.color ?? nextTrackColor(drawnTracks().map((t) => t.color));

        rememberTrack({
          trackId,
          esn,
          name: name || esn,
          data: trackLine,
          color: trackColor,
          halo: haloColorFor(trackColor),
          span,
        });

        // Drop shadow: a wider, blurred casing beneath the line. Mapbox line
        // layers have no shadow property, and a track drawn in a single colour
        // disappears wherever the basemap happens to match it. The halo is
        // chosen against the track's own luminance, so one of the pair always
        // contrasts — on the dark basemap and on a light one alike.

        // Hand the track to the drawer (#802). The registry layer it creates
        // performs the actual addSource/addLayer, which is what gives the track
        // the drawer's visibility, opacity and z-order controls — previously it
        // was drawn here and the drawer never knew it existed.
        dispatchAffTrackDrawn({
          trackId,
          esn,
          aircraftName: name || esn,
          data: trackLine,
          color: trackColor,
          halo: haloColorFor(trackColor),
          span,
        });

        // Asked to SHOW the track — so move the map to it. Drawing a line
        // outside the current viewport looks identical to drawing nothing.
        // Only for explicit show requests; the live layer's own click
        // behaviour must not yank the map out from under the user.
        if (fitToTrack) {
          const coords: [number, number][] = trackLine?.geometry?.coordinates ?? [];
          if (coords.length > 0) {
            const lons = coords.map((c) => c[0]);
            const lats = coords.map((c) => c[1]);
            map.fitBounds(
              [
                [Math.min(...lons), Math.min(...lats)],
                [Math.max(...lons), Math.max(...lats)],
              ],
              { padding: 80, maxZoom: 12, duration: 800 },
            );
          }
        }

        // Click and hover on the line belong to the track's registry layer
        // (AffTrackLayer), bound once per layer and unbound with it (#866 M3).

        log.debug(`Added track for aircraft ${esn}`);
      } else {
        log.error('Failed to load aircraft track:', data.msg);
      }
    } catch (error) {
      log.error('Error fetching aircraft track:', error);
    }
  }
